import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, Building2, X, Trash2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { ConfirmDialog } from '@/components/custom/ConfirmDialog';
import {
  fetchOperatorsFull,
  createOperatorApi,
  updateOperatorApi,
  deleteOperatorApi,
} from '@/hooks/useAdmin';
import type { OperatorAdminItem, CreateOperatorRequest, UpdateOperatorRequest } from '@uniticket/shared';

export function OperatorSettings() {
  const { t } = useTranslation('admin');
  const [operators, setOperators] = useState<OperatorAdminItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<OperatorAdminItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<OperatorAdminItem | null>(null);

  const loadData = useCallback(async () => {
    try {
      const res = await fetchOperatorsFull();
      setOperators(res.data);
    } catch (err: any) {
      toast.error(err.message || t('operators.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { loadData(); }, [loadData]);

  function handleAdd() {
    setEditing(null);
    setDialogOpen(true);
  }

  function handleEdit(op: OperatorAdminItem) {
    setEditing(op);
    setDialogOpen(true);
  }

  async function handleSave(data: CreateOperatorRequest | UpdateOperatorRequest) {
    try {
      if (editing) {
        await updateOperatorApi(editing.id, data as UpdateOperatorRequest);
        toast.success(t('operators.updated'));
      } else {
        await createOperatorApi(data as CreateOperatorRequest);
        toast.success(t('operators.created'));
      }
      setDialogOpen(false);
      loadData();
    } catch (err: any) {
      toast.error(err.message || t('operators.saveError'));
    }
  }

  async function handleDeleteConfirm() {
    if (!deleteTarget) return;
    try {
      await deleteOperatorApi(deleteTarget.id);
      toast.success(t('operators.deleted'));
      setDeleteTarget(null);
      loadData();
    } catch (err: any) {
      toast.error(err.message || t('operators.deleteError'));
      setDeleteTarget(null);
    }
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-10 w-32" />
        </div>
        {[1, 2, 3].map((i) => <Skeleton key={i} className="h-14 w-full" />)}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-brand-gradient flex items-center justify-center">
            <Building2 className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-800">{t('operators.title')}</h2>
            <p className="text-sm text-slate-500">{t('operators.count', { count: operators.length })}</p>
          </div>
        </div>
        <Button onClick={handleAdd} className="bg-brand-gradient text-white hover:opacity-90">
          <Plus className="w-4 h-4 mr-2" />
          {t('operators.add')}
        </Button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">{t('operators.cols.name')}</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">{t('operators.cols.code')}</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">{t('operators.cols.contactEmail')}</th>
                <th className="text-center px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">{t('operators.cols.status')}</th>
                <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">{t('operators.cols.users')}</th>
                <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">{t('operators.cols.tickets')}</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">{t('operators.cols.created')}</th>
                <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">{t('operators.cols.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {operators.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-slate-400">
                    {t('operators.empty')}
                  </td>
                </tr>
              ) : (
                operators.map((op) => (
                  <tr key={op.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 font-medium text-slate-700">{op.name}</td>
                    <td className="px-4 py-3 font-mono text-slate-600">{op.code}</td>
                    <td className="px-4 py-3 text-slate-600">{op.contactEmail || '—'}</td>
                    <td className="px-4 py-3 text-center">
                      {op.isActive ? (
                        <span className="inline-flex items-center text-xs font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">Active</span>
                      ) : (
                        <span className="inline-flex items-center text-xs font-semibold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">Inactive</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-600">{op.userCount}</td>
                    <td className="px-4 py-3 text-right text-slate-600">{op.ticketCount}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{new Date(op.createdAt).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleEdit(op)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-brand-rose hover:bg-brand-rose/5 transition-colors"
                          title={t('operators.edit')}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeleteTarget(op)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                          title={t('operators.deleteTitle')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Dialog */}
      {dialogOpen && (
        <OperatorDialog
          editing={editing}
          onSave={handleSave}
          onClose={() => setDialogOpen(false)}
        />
      )}
      {deleteTarget && (
        <ConfirmDialog
          title={t('operators.deleteTitle')}
          message={`You are about to permanently delete the operator "${deleteTarget.name}" (${deleteTarget.code}). This action cannot be undone.`}
          details={[
            ...(deleteTarget.userCount > 0 ? [`${deleteTarget.userCount} user(s) still assigned — delete or reassign them first`] : []),
            ...(deleteTarget.ticketCount > 0 ? [`${deleteTarget.ticketCount} ticket(s) still linked — these must be removed first`] : []),
            ...(deleteTarget.userCount === 0 && deleteTarget.ticketCount === 0
              ? ['This operator has no users or tickets and can be safely deleted.']
              : ['You must remove all dependencies before this operator can be deleted.']),
          ]}
          confirmLabel={t('operators.delete')}
          cancelLabel={t('operators.cancel')}
          variant="danger"
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

// ── Operator Dialog ─────────────────────────────────────────

interface OperatorDialogProps {
  editing: OperatorAdminItem | null;
  onSave: (data: CreateOperatorRequest | UpdateOperatorRequest) => Promise<void>;
  onClose: () => void;
}

function OperatorDialog({ editing, onSave, onClose }: OperatorDialogProps) {
  const { t } = useTranslation('admin');
  const [name, setName] = useState(editing?.name || '');
  const [code, setCode] = useState(editing?.code || '');
  const [contactEmail, setContactEmail] = useState(editing?.contactEmail || '');
  const [slaTimezone, setSlaTimezone] = useState(editing?.slaTimezone || 'Europe/Brussels');
  const [allowedTicketTypes, setAllowedTicketTypes] = useState<string[]>(
    editing?.allowedTicketTypes ?? ['OPEN_ORDER', 'ADDRESS_MANAGEMENT', 'SUPPORT_REQUEST'],
  );
  // Off for a brand-new operator: Unifiber's passive network is never exposed by default.
  const [canViewNetwork, setCanViewNetwork] = useState(editing?.canViewNetwork ?? false);
  // Off for a brand-new operator too: the contractors' remarks are written for Unifiber.
  const [canViewDelayRemarks, setCanViewDelayRemarks] = useState(editing?.canViewDelayRemarks ?? false);
  const [isActive, setIsActive] = useState(editing?.isActive ?? true);
  const [saving, setSaving] = useState(false);

  function toggleTicketType(type: string) {
    setAllowedTicketTypes((prev) => {
      if (prev.includes(type)) {
        // Don't allow removing the last one
        if (prev.length <= 1) return prev;
        return prev.filter((t) => t !== type);
      }
      return [...prev, type];
    });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      if (!name || !code) {
        toast.error(t('operators.nameCodeRequired'));
        return;
      }
      if (editing) {
        await onSave({
          name,
          code: code.toUpperCase(),
          contactEmail: contactEmail || undefined,
          slaTimezone,
          isActive,
          allowedTicketTypes,
          canViewNetwork,
          canViewDelayRemarks,
        } satisfies UpdateOperatorRequest);
      } else {
        await onSave({
          name,
          code: code.toUpperCase(),
          contactEmail: contactEmail || undefined,
          slaTimezone,
          allowedTicketTypes,
          canViewNetwork,
          canViewDelayRemarks,
        } satisfies CreateOperatorRequest);
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        <div className="gradient-band px-6 py-4 flex items-center justify-between">
          <h3 className="text-sm font-bold text-white uppercase tracking-wide">
            {editing ? t('operators.editTitle') : t('operators.createTitle')}
          </h3>
          <button onClick={onClose} className="text-white/60 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body scrolls, gradient header stays put — same pattern as the User Management dialog.
            Without the cap the panel simply overflows the viewport with no way to reach the title
            bar or the Cancel/Update buttons. */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5 max-h-[calc(100vh-8rem)] overflow-y-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>{t('operators.fields.name')}</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} required maxLength={100} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('operators.fields.code')}</Label>
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ''))}
                required
                maxLength={20}
                placeholder="e.g. PRX"
                className="font-mono"
              />
              <p className="text-xs text-slate-400">{t('operators.fields.codeHint')}</p>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>{t('operators.fields.contactEmail')} <span className="text-slate-400 font-normal">({t('operators.optional')})</span></Label>
            <Input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} maxLength={255} />
          </div>

          <div className="space-y-1.5">
            <Label>{t('operators.fields.timezone')}</Label>
            <Input value={slaTimezone} onChange={(e) => setSlaTimezone(e.target.value)} maxLength={50} />
          </div>

          {/* Allowed Ticket Types */}
          <div className="space-y-2">
            <Label>{t('operators.fields.allowedTicketTypes')}</Label>
            <div className="space-y-2">
              {[
                { value: 'OPEN_ORDER', label: 'Open Order' },
                { value: 'ADDRESS_MANAGEMENT', label: 'Address Management' },
                { value: 'SUPPORT_REQUEST', label: 'Support Request' },
                // This checkbox is the operator ceiling — it must be ticked before a group can be
                // granted Build, and unticking it hides every Build ticket from this operator's
                // non-admin users immediately, with no deploy. It is the feature's kill switch.
                { value: 'BUILD', label: 'Build Management' },
              ].map(({ value, label }) => (
                <label key={value} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={allowedTicketTypes.includes(value)}
                    onChange={() => toggleTicketType(value)}
                    className="w-4 h-4 rounded border-slate-300 text-brand-rose focus:ring-brand-rose"
                  />
                  <span className="text-sm text-slate-700">{label}</span>
                </label>
              ))}
            </div>
            <p className="text-xs text-slate-400">{t('operators.fields.allowedTicketTypesHint')}</p>
          </div>

          {/* Network data visibility — governs the Cocon layers on BOTH ticket maps (the address
              enrichment map and the Build area map). Internal Unifiber users always see them;
              this grants them to an external operator's users. */}
          <div className="space-y-2">
            <Label>{t('operators.fields.networkVisibility')}</Label>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={canViewNetwork}
                onChange={() => setCanViewNetwork((v) => !v)}
                className="w-4 h-4 mt-0.5 rounded border-slate-300 text-brand-rose focus:ring-brand-rose"
              />
              <span className="text-sm text-slate-700">{t('operators.fields.canViewNetwork')}</span>
            </label>
            {canViewNetwork && (
              <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800">{t('operators.fields.canViewNetworkWarning')}</p>
              </div>
            )}
            <p className="text-xs text-slate-400">{t('operators.fields.canViewNetworkHint')}</p>
          </div>

          {/* Delay remarks — the contractor's free text on the delays of this operator's own orders,
              in order history (Address Information and a ticket's address panel). The server decides;
              unticking it hides the remarks on the next request, with no deploy. */}
          <div className="space-y-2">
            <Label>{t('operators.fields.delayRemarkVisibility')}</Label>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={canViewDelayRemarks}
                onChange={() => setCanViewDelayRemarks((v) => !v)}
                className="w-4 h-4 mt-0.5 rounded border-slate-300 text-brand-rose focus:ring-brand-rose"
              />
              <span className="text-sm text-slate-700">{t('operators.fields.canViewDelayRemarks')}</span>
            </label>
            {canViewDelayRemarks && (
              <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800">{t('operators.fields.canViewDelayRemarksWarning')}</p>
              </div>
            )}
            <p className="text-xs text-slate-400">{t('operators.fields.canViewDelayRemarksHint')}</p>
          </div>

          {/* Active toggle (edit only) */}
          {editing && (
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setIsActive(!isActive)}
                className={`relative w-11 h-6 rounded-full transition-colors ${isActive ? 'bg-green-500' : 'bg-slate-300'}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${isActive ? 'translate-x-5' : ''}`} />
              </button>
              <Label className="cursor-pointer" onClick={() => setIsActive(!isActive)}>
                {isActive ? t('operators.active') : t('operators.inactive')}
              </Label>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>{t('operators.cancel')}</Button>
            <Button type="submit" disabled={saving} className="bg-brand-gradient text-white hover:opacity-90">
              {saving ? t('operators.saving') : editing ? t('operators.update') : t('operators.create')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
